import React from 'react';
import { NavLink } from 'react-router-dom';
import { motion } from 'motion/react';
import CTAButton from '../../../../shared/components/buttons/CTAButton';
import SecondaryButton from '../../../../shared/components/buttons/SecondaryButton';
import './Hero.css';

const heroMeta = ['Free to start', 'Setup in 10 min', 'Stripe-verified payouts'];

const floatingBlobConfigs = [
  {
    size: 620,
    position: { top: '2%', left: '-5%' },
    gradient:
      'radial-gradient(circle at 35% 35%, rgba(255, 235, 218, 0.6) 0%, rgba(255, 245, 234, 0.35) 40%, transparent 80%)',
    transition: { duration: 26, delay: 0 },
    animate: { x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.1, 1] },
  },
  {
    size: 560,
    position: { top: '12%', right: '-8%' },
    gradient:
      'radial-gradient(circle at 60% 40%, rgba(168, 213, 240, 0.4) 0%, rgba(211, 232, 252, 0.25) 45%, transparent 90%)',
    transition: { duration: 30, delay: 2 },
    animate: { x: [0, -40, 0], y: [0, 35, 0], scale: [1, 1.15, 1] },
  },
  {
    size: 520,
    position: { top: '55%', left: '32%' },
    gradient:
      'radial-gradient(circle at 50% 50%, rgba(214, 152, 118, 0.25) 0%, rgba(222, 176, 148, 0.18) 45%, transparent 90%)',
    transition: { duration: 32, delay: 4 },
    animate: { x: [0, 40, 0], y: [0, -40, 0], scale: [1, 1.1, 1] },
  },
];

const HeroMedia = () => (
  <div className="flex-1">
    <div className="relative overflow-hidden rounded-[2.5rem] border border-white/30 bg-transparent shadow-[0_45px_120px_-60px_rgba(15,20,40,0.55)] backdrop-blur-xl">
      <img
        src="/images/kinn-mockup.png"
        alt="Preview of a Kinn storefront"
        className="h-[420px] w-full object-cover"
      />
      <motion.div
        aria-hidden
        className="absolute inset-0 opacity-0"
        style={{
          background: 'linear-gradient(110deg, transparent 33%, rgba(255,255,255,0.35) 50%, transparent 66%)',
          backgroundSize: '200% 100%',
        }}
        animate={{ opacity: [0, 1, 0], backgroundPosition: ['200% 0', '-200% 0'] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/10" />
    </div>
  </div>
);

const HeroHighlights = () => (
  <div className="mt-6 flex flex-wrap gap-5 text-sm font-medium text-neutral-600">
    {heroMeta.map((item) => (
      <span key={item} className="inline-flex items-center gap-2">
        <span className="hero-meta-badge">✓</span>
        {item}
      </span>
    ))}
  </div>
);

const Hero = () => (
  <section
    className="relative overflow-hidden px-6 pb-20 pt-32 lg:px-12 lg:pb-28 lg:pt-40"
    style={{
      background: 'linear-gradient(120deg, #fef8f1 0%, #fbf3ea 45%, #f2f5fb 100%)',
    }}
  >
    {floatingBlobConfigs.map((blob) => (
      <motion.div
        key={blob.position.top}
        className="pointer-events-none absolute rounded-full opacity-70"
        style={{
          width: blob.size,
          height: blob.size,
          ...blob.position,
          background: blob.gradient,
          filter: 'blur(90px)',
        }}
        animate={blob.animate}
        transition={{ ...blob.transition, repeat: Infinity, ease: 'easeInOut' }}
      />
    ))}

    <div className="relative mx-auto flex max-w-6xl flex-col gap-12 px-0 lg:flex-row lg:items-center lg:px-0">
      <div className="flex-1 space-y-7 py-6">
        <span className="hero-eyebrow-pill">
          Built for home cooks &amp; food makers
        </span>
        <div>
          <h1
            className="font-display font-semibold leading-tight text-text-primary"
            style={{ fontSize: 'clamp(2.8rem, 5vw, 3.7rem)' }}
          >
            Your Food Business,
            <br /> Officially Open.
          </h1>
          <div className="mt-5 h-1 w-16 rounded-full bg-[#d59475]" />
        </div>
        <p className="text-lg leading-7 text-text-secondary">
          Kinn makes it easy to sell food online with built-in compliance, beautiful storefronts, and AI-powered tools
          that handle the complex stuff so you can focus on what you love.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <CTAButton as={NavLink} to="/seller/get-started" size="lg">
            Start for free
          </CTAButton>
          <SecondaryButton size="lg" as={NavLink} to="/seller/get-started">
            See how it works
          </SecondaryButton>
        </div>
        <HeroHighlights />
      </div>

      <HeroMedia />
    </div>
  </section>
);

export default Hero;
